import BaseTooltip from 'components/BaseTooltip'
import FullPageLoading from 'components/FullPageLoading'
import Icon from 'components/Icon'
import { currentUserSelector } from 'features/auth/authSlice'
import {
    eventPreviewSelector,
    updateShowAttendeesOutside,
    updateShowCalendarIcon,
    updateShowSpeakerIcon,
} from 'features/auth/eventPreviewSlice'
import { updateEvent } from 'features/generalsettings/graphql/mutation'
import { showErrorNotification } from 'features/notifications/notificationsSlice'
import { useEventSlug } from 'hooks'
import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import { colors, _ } from 'utils'

const S = {
    Divider: styled.div`
        width: 0;
        height: 28px;
        margin: 0 10px;
        border-left: 1px solid ${colors.iconOpacity(0.1)};
    `,
    InfoPlaceholderContainer: styled.div`
        display: flex;
        justify-content: flex-end;
        align-items: center;
        flex: 1;
    `,
    UserImage: styled.img`
        width: 35px;
        height: 35px;
        border-radius: 50%;
        object-fit: cover;
        object-position: center;
        margin-left: 8px;
    `,
    NonInteractiveIcons: styled.div`
        display: flex;
        align-items: center;
        opacity: 0.5;
    `
}

const InfoPlaceholder = () => {
    const dispatch = useDispatch()
    const user = useSelector(currentUserSelector)
    const eventPreview = useSelector(eventPreviewSelector)
    const [isUploading, setUploading] = useState(false)
    const eventSlug = useEventSlug()

    const updateVirtualEventConfigs = async (type: 'attendee' | 'speaker' | 'calendar') => {
        setUploading(true)
        let response

        if (type === 'attendee') {
            response = await updateEvent(eventSlug, {
                virtual: {
                    showAttendeeListOutside: !eventPreview?.show_attendee_list_outside,
                },
            })
            dispatch(updateShowAttendeesOutside(!eventPreview?.show_attendee_list_outside))
        } else if (type === 'speaker') {
            response = await updateEvent(eventSlug, {
                virtual: {
                    showSpeakerIcon: !eventPreview?.show_speaker_icon,
                },
            })
            dispatch(updateShowSpeakerIcon(!eventPreview?.show_speaker_icon))
        } else {
            response = await updateEvent(eventSlug, {
                virtual: {
                    showCalendarIcon: !eventPreview?.show_calendar_icon,
                },
            })
            dispatch(updateShowCalendarIcon(!eventPreview?.show_calendar_icon))
        }

        if (response.errors.length) {
            dispatch(showErrorNotification(response.errors))
            return
        }
        setUploading(false)
    }

    return (
        <S.InfoPlaceholderContainer>
            <BaseTooltip text={_('navigation.show_speaker_icon.toggle_helper_text')}>
                <Icon
                    icon="performer"
                    isActive={eventPreview?.show_speaker_icon}
                    activeColor={colors.primary}
                    onClick={() => updateVirtualEventConfigs('speaker')}
                    size={30}
                    style={{ marginBottom: -10, marginRight: -5 }}
                />
            </BaseTooltip>
            <S.Divider />
            <BaseTooltip text={_('navigation.show_calendar_icon.toggle_helper_text')}>
                <Icon
                    icon="timetable"
                    isActive={eventPreview?.show_calendar_icon}
                    activeColor={colors.primary}
                    onClick={() => updateVirtualEventConfigs('calendar')}
                />
            </BaseTooltip>
            <S.Divider />
            <BaseTooltip text={_('navigation.show_attendee_list_outside.toggle_helper_text')}>
                {eventPreview?.show_attendee_list_outside_option === 'ALL' ? (
                    <Icon
                        icon="group"
                        isActive={eventPreview?.show_attendee_list_outside}
                        activeColor={colors.primary}
                        onClick={() => updateVirtualEventConfigs('attendee')}
                    />
                ) : (
                    <Icon
                        icon="group"
                        isActive={eventPreview?.show_attendee_list_outside}
                        activeColor={colors.primary}
                    />
                )}
            </BaseTooltip>
            <S.Divider />
            <S.NonInteractiveIcons>
                <Icon icon="email" />
                <S.Divider />
                <S.UserImage src={user?.mugshot} />
                <Icon icon="chevronDown" size={18} />
                <S.Divider />
                <Icon icon="help" />
                <S.Divider />
                <Icon icon="expand" />
            </S.NonInteractiveIcons>
            {isUploading && <FullPageLoading isAbsolute fullWidth />}
        </S.InfoPlaceholderContainer>
    )
}

export default InfoPlaceholder
